/**
 * Samadhan Live Stats — Inline header stats panel
 * ONLY rendered inside /samadhan page hero. Not global.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp } from 'lucide-react';
import api from '../utils/api';

const useCountdown = (target) => {
  const [t, setT] = useState(() => Math.max(0, new Date(target).getTime() - Date.now()));
  useEffect(() => {
    const id = setInterval(() => setT(Math.max(0, new Date(target).getTime() - Date.now())), 1000);
    return () => clearInterval(id);
  }, [target]);
  const d = Math.floor(t / 86400000);
  const h = Math.floor((t % 86400000) / 3600000);
  const m = Math.floor((t % 3600000) / 60000);
  const s = Math.floor((t % 60000) / 1000);
  return { d, hms: `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` };
};

const fmtAT = (n) => {
  const v = Number(n || 0);
  if (v >= 10000000) return `${(v / 10000000).toFixed(2)} Cr`;
  if (v >= 100000) return `${(v / 100000).toFixed(2)} L`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return String(v);
};

const SamadhanLiveTicker = () => {
  const [stats, setStats] = useState(null);
  const [founders, setFounders] = useState({ slots_available: 10, total_slots: 10 });
  const { d: days, hms } = useCountdown('2031-01-01');

  useEffect(() => {
    let mounted = true;
    const load = () => {
      api.get('/samadhan/stats').then((r) => { if (mounted) setStats(r.data); }).catch(() => {});
      api.get('/samadhan/founders-wall?limit=10').then((r) => { if (mounted) setFounders(r.data); }).catch(() => {});
    };
    load();
    const id = setInterval(load, 60000);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  if (!stats) return null;

  return (
    <>
      <style>{`
        @keyframes samadhan-stat-blink { 0%,100% { opacity: 1; text-shadow: 0 0 4px currentColor; } 50% { opacity: 0.55; text-shadow: none; } }
        .samadhan-stat-blink { animation: samadhan-stat-blink 1.4s ease-in-out infinite; }
      `}</style>

      <div
        className="w-full rounded-2xl border-2 border-amber-400/60 bg-gradient-to-br from-slate-950/80 via-indigo-950/70 to-slate-950/80 backdrop-blur-md overflow-hidden"
        data-testid="samadhan-live-ticker"
      >
        <div className="flex items-center justify-between px-3 py-1.5 bg-gradient-to-r from-amber-500/25 to-orange-500/25 border-b border-amber-400/40">
          <div className="flex items-center gap-1.5 min-w-0">
            <TrendingUp className="w-3.5 h-3.5 text-amber-300 flex-shrink-0" />
            <span className="text-[10px] sm:text-xs font-black uppercase tracking-widest text-amber-200">📊 STATS ANALYTICS · LIVE</span>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 p-3 text-[11px] sm:text-xs">
          <div className="flex flex-col items-center bg-white/5 rounded-lg p-2 border border-emerald-400/20">
            <span className="text-[9px] uppercase text-emerald-300/80 tracking-wider">💰 Reservoir</span>
            <b className="samadhan-stat-blink font-mono text-emerald-300 text-sm mt-0.5">{fmtAT(stats.reservoir_balance)} AT</b>
          </div>
          <div className="flex flex-col items-center bg-white/5 rounded-lg p-2 border border-fuchsia-400/20">
            <span className="text-[9px] uppercase text-fuchsia-300/80 tracking-wider">📈 Progress</span>
            <b className="samadhan-stat-blink font-mono text-fuchsia-300 text-sm mt-0.5">{stats.progress_pct}%</b>
          </div>
          <div className="flex flex-col items-center bg-white/5 rounded-lg p-2 border border-sky-400/20">
            <span className="text-[9px] uppercase text-sky-300/80 tracking-wider">🤝 Donors</span>
            <b className="samadhan-stat-blink font-mono text-sky-300 text-sm mt-0.5">{stats.unique_donors}</b>
          </div>
          <div className="flex flex-col items-center bg-white/5 rounded-lg p-2 border border-red-400/30">
            <span className="text-[9px] uppercase text-red-300/80 tracking-wider">🏛️ Founder Seats</span>
            <b className="samadhan-stat-blink font-mono text-red-300 text-sm mt-0.5">{founders.slots_available}/{founders.total_slots}</b>
          </div>
          <div className="flex flex-col items-center bg-white/5 rounded-lg p-2 border border-amber-400/40 col-span-2 sm:col-span-1">
            <span className="text-[9px] uppercase text-amber-300/80 tracking-wider">🎉 Grand Opening</span>
            <div className="flex items-baseline gap-1 mt-0.5">
              <b className="samadhan-stat-blink font-mono text-amber-200 text-sm">{days}<span className="text-[9px] opacity-70">d</span></b>
              <span className="samadhan-stat-blink font-mono text-amber-200/90 text-[10px]">{hms}</span>
            </div>
            <div className="text-[9px] text-slate-400">01/01/2031</div>
          </div>
        </div>

        <Link
          to="/samadhan"
          data-testid="samadhan-stats-cta"
          className="block text-center py-1.5 bg-gradient-to-r from-amber-500 to-yellow-500 text-black font-black text-[10px] sm:text-xs uppercase tracking-wider hover:from-amber-400 hover:to-yellow-400"
        >🙏 Donate · Get Certificate →</Link>
      </div>
    </>
  );
};

export default SamadhanLiveTicker;
